import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StoreContext } from '../../../models/store/context';
import { saveSession } from '../../../services/session/save';
import { log } from '@domgen/shared/utils/log';
import { StoreEvent } from '../../../enums/storeEvent';

/**
 * Converts existing quote data into a registration-only confirmation payload.
 *
 * Specifically this is for when the user has selected to register only and not proceed with a quote
 *
 * Navigates to confirmation and persists the derived reg-only data.
 *
 * @param context Current store context.
 * @param _event Unused event payload.
 * @param enqueue Store enqueue helpers used for navigation emission.
 * @returns Updated context with derived reg-only data.
 */
export const regOnly = <T extends EventObject = EventObject>(
  context: StoreContext,
  _event: T,
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.REG_ONLY });

  if (!context.quote || !context.quote.data) {
    log.debug({ message: 'No quote data available to derive reg-only response' });
    enqueue.emit.navigate({ path: AppRoute.ERROR });
    return context;
  }

  const updatedContext = {
    ...context,
    confirmation: {
      ...context.confirmation,
      regOnly: {
        personId: context.quote.data.personId,
        items: context.quote.data.items,
      },
    },
  } as StoreContext;
  enqueue.emit.navigate({ path: AppRoute.CONFIRMATION });
  saveSession(updatedContext);
  return updatedContext;
};
